import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Tooltip,
    MenuItem,
    Select,
    FormControl,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import Styles from './RevealAnswerKey.module.scss';

import { atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { DoneAll } from '@mui/icons-material';
import axios from "axios";



export default function RevealAnswerKey({
    showAnswerKeyDialog,
    setShowAnswerKeyDialog,
    steps,
    currentStep,
    language,
    nodeId,
    nodeName,
    labTitle,
    enableSurvey = true,
}) {

    const [revealAnswer, setRevealAnswer] = useState(false);
    const [surveyAnswered, setSurveyAnswered] = useState(false);
    const [reason, setReason] = useState('facing_difficulty');
    const [otherText, setOtherText] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(currentStep);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setSelectedIndex(currentStep)
    }, [currentStep])

    useEffect(() => {
        if (showAnswerKeyDialog) {
            const persisted = sessionStorage.getItem(nodeId);
            if (persisted) {
                setSurveyAnswered(true);
            }
        }
    }, [showAnswerKeyDialog, nodeId]);

    const handleConfirmReveal = async () => {
        if (!surveyAnswered && enableSurvey) {
            try {
                const reasonMap = {
                    facing_difficulty: "Facing difficulty with this step",
                    time_constraint: "Time constraint",
                    concept_unknown: "Concept unknown / needs review",
                    verify_answer: "Just verifying my answer",
                    other: "Other"
                };
                if (reason === 'other' && (!otherText || otherText.trim().length === 0)) {
                    return;
                }

                const reasonText = reason === 'other' ? otherText.trim() : reasonMap[reason];
                const comment = `Reason To Reveal the Answer key: ${reasonText}`;
                const computedNodeTitle = nodeName ?? null;
                const computedLabTitle = labTitle ?? null;

                const payload = {
                    rating: 'survey',
                    comment,
                    title: computedNodeTitle,
                    lab_title: computedLabTitle
                };
                await axios.post('/api/assessment/survey', payload);
                setSurveyAnswered(true);
                sessionStorage.setItem(nodeId, true);
            } catch (error) {
            }
        }
        setRevealAnswer(true);
    };

    const handleCloseAnswerKey = () => {
        setShowAnswerKeyDialog(false)
        setReason('facing_difficulty');
        setRevealAnswer(false);
        setOtherText('');
        setSelectedIndex(currentStep)
    }

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text || '');
            setCopied(true)
            setTimeout(() => {
                setCopied(false)
            }, 1000);
        } catch {
            console.error('Clipboard error ') //TODO need to be changed to a toast warning users copy failed
        }
    }

    return (
        <Dialog id="revealAnswerDialog" open={!!showAnswerKeyDialog} onClose={handleCloseAnswerKey} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle id="revealAnswerTitle" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!revealAnswer ? <VisibilityIcon fontSize="small" /> : null}
                <Typography variant="h6" component="span">
                    {!revealAnswer ? 'Reveal answer?' : 'Answer'}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton id="closeButton" size="small" onClick={handleCloseAnswerKey} aria-label="Close" className={Styles.pillIcon}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {!revealAnswer ? (
                    <div id="reasonRoot" className={Styles.reasonRoot}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                        You’re about to reveal the answer.
                        Before you do, take a moment to revisit the topics and try the assessment again.
                        If you’re still stuck, that’s okay — go ahead and view the answer.
                        </Typography>

                        {!surveyAnswered && enableSurvey && (
                            <div id="reasonForm" className={Styles.reasonForm}>
                                <div id="reasonGroup" className={Styles.reasonGroup}>
                                    <label className={Styles.reasonLabel}>
                                        <input
                                            id="reasonFacingDifficulty"
                                            type="radio"
                                            name="reveal_reason"
                                            value="facing_difficulty"
                                            checked={reason === 'facing_difficulty'}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                        Facing difficulty with this step
                                    </label>
                                    <label className={Styles.reasonLabel}>
                                        <input
                                            id="reasonTimeConstraint"
                                            type="radio"
                                            name="reveal_reason"
                                            value="time_constraint"
                                            checked={reason === 'time_constraint'}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                        Time constraint
                                    </label>
                                    <label className={Styles.reasonLabel}>
                                        <input
                                            id="reasonConceptUnknown"
                                            type="radio"
                                            name="reveal_reason"
                                            value="concept_unknown"
                                            checked={reason === 'concept_unknown'}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                        Concept unknown / needs review
                                    </label>
                                    <label className={Styles.reasonLabel}>
                                        <input
                                            id="reasonVerifyAnswer"
                                            type="radio"
                                            name="reveal_reason"
                                            value="verify_answer"
                                            checked={reason === 'verify_answer'}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                        Just verifying my answer
                                    </label>
                                    <label className={Styles.reasonLabel}>
                                        <input
                                            id="reasonOther"
                                            type="radio"
                                            name="reveal_reason"
                                            value="other"
                                            checked={reason === 'other'}
                                            onChange={(e) => {
                                                setReason(e.target.value);
                                            }}
                                        />
                                        Other (please specify)
                                    </label>
                                    {reason === 'other' && (
                                        <>
                                            <input
                                                id="reasonOtherText"
                                                className={Styles.reasonOther}
                                                placeholder="Tell us briefly…"
                                                value={otherText}
                                                onChange={(e) => {
                                                    setOtherText(e.target.value);
                                                }}
                                                aria-label="Specify other reason"
                                            />
                                            {(reason === "other") && (
                                                <small style={{ color: '#b91c1c' }}>
                                                    Please add a short note before continuing.
                                                </small>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <Box>
                        <div id="answerHeaderRow" className={Styles.answerHeaderRow}>
                            <div id="answerTitle" className={Styles.answerTitle}>{steps[selectedIndex]?.timeline || 'No answer'}</div>

                            <div id="stepPicker" className={Styles.stepPicker}>
                                <Typography variant="caption" className={Styles.stepPickerLabel}>
                                    Jump to step
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 240 }}>
                                    <Select
                                        id="stepPickerSelect"
                                        value={selectedIndex}
                                        onChange={(e) => setSelectedIndex(e.target.value)}
                                    >
                                        {steps.map((step, index) => (
                                            <MenuItem key={index} value={index}>
                                                {`Step ${index + 1}: ${step?.timeline}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>

                        {!steps[selectedIndex]?.ans_key ? (
                            <Typography variant="body2" color="text.secondary">
                                No answer available for this step.
                            </Typography>
                        ) : (
                            <>
                                <div id="answerBlock" className={Styles.answerBlock}>
                                    <div id="copyBtnContainer" className={Styles.copyBtnContainer}>
                                        <Tooltip title={copied ? "Copied" : "Copy answer"}>
                                            <IconButton
                                                id="copyBtn"
                                                size="small"
                                                onClick={() => handleCopy(steps[selectedIndex].ans_key)}
                                                aria-label="Copy answer"
                                                sx={{ color: '#1450a0' }}
                                            >
                                                {copied ? <DoneAll fontSize='inherit' /> : <ContentCopyIcon fontSize='inherit' />}
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                    <SyntaxHighlighter
                                        language={language || "plaintext"}
                                        style={atomOneLight}
                                        showLineNumbers
                                        customStyle={{
                                            width: '100%',
                                            borderRadius: '8px',
                                            overflow: 'auto',
                                            scrollbarWidth: 'thin',
                                            WebkitOverflowScrolling: 'touch',
                                        }}
                                        // wrapLongLines={true}
                                        onCopy={() => handleCopy(steps[selectedIndex]?.ans_key)}
                                    >
                                        {steps[selectedIndex]?.ans_key}
                                    </SyntaxHighlighter>
                                </div>
                                <style>
                                    {`
                                        /* WebKit Scrollbar Styles */
                                        ::-webkit-scrollbar {
                                            width: 7.5px;
                                            height: 7.5px;
                                            background-color: #555;
                                            border-radius: 0.5em;
                                        }

                                        ::-webkit-scrollbar-thumb {
                                            border-radius: 0.5em;
                                            background-color: lightgray;
                                        }

                                        scrollbar-width: thin;
                                        scrollbar-color: darkgray lightgray;
                                    `}
                                </style>
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                {!revealAnswer && (
                    <>
                        <AivagamButton
                            id="cancelRevealBtn"
                            type="cancel"
                            buttonText={'Cancel'}
                            onClick={handleCloseAnswerKey}
                        />
                        <AivagamButton
                            id="confirmRevealBtn"
                            onClick={handleConfirmReveal}
                            buttonText={'Reveal'}
                            disabled={!steps?.length}
                            type="confirm"
                            classStyles={{ width: '7em' }}
                        />
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
